import Decimal from 'decimal.js';

const defaultCurrency = 'DKK' as const;

type FormatCurrencyOptions = {
  appendCurrency?: boolean | string | null;
  showZero?: boolean;
  minimumFractionDigits?: number;
  currency?: string;
};
/**
 * Formats a numeric value as a currency string.
 *
 * @param {number|undefined} value - The numeric value to format as currency. Optional and defaults to undefined.
 * @param {FormatCurrencyOptions|undefined} options - Optional options object.
 * @returns {string|null} The formatted currency string, or null if the input value is falsy.
 */
export const formatCurrency = (
  value?: number | null,
  options?: FormatCurrencyOptions,
): string => {
  const currencyFormatter = new Intl.NumberFormat('da-DK', {
    currency: options?.currency ?? defaultCurrency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
  });
  return (value === 0 && options?.showZero) || value
    ? `${currencyFormatter.format(value)}${
        options?.appendCurrency
          ? typeof options.appendCurrency === 'string'
            ? ` ${options.appendCurrency}`
            : ` ${defaultCurrency}`
          : ''
      }`
    : '—';
};

/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param {number} num - The number to be clamped.
 * @param {number} min - The minimum value to clamp to.
 * @param {number} max - The maximum value to clamp to.
 * @returns {number} The clamped number.
 */
export const clamp = (num: number, min: number, max: number): number => {
  if (Number.isNaN(num) || Number.isNaN(min) || Number.isNaN(max)) {
    return NaN;
  }
  return num < min ? min : num > max ? max : num;
};

/**
 * Formats a percentage amount into Danish format.
 * @param {number} percentage - The percentage to format (e.g. 50 for 50%).
 * @returns {string} The formatted percentage string.
 */
export function formatPercentage(
  percentage: number | null | undefined,
  digits = 2,
  removeUnit?: boolean,
): string {
  if (
    percentage === undefined ||
    percentage === null ||
    typeof percentage !== 'number' ||
    Number.isNaN(percentage)
  )
    return '—';
  const formatted = new Intl.NumberFormat('da-DK', {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(percentage / 100);
  return removeUnit ? formatted.replace('%', '') : formatted;
}

/**
 * Formats a basis points value as percentage in Danish format.
 * @param {number} value - Basis points value (e.g. 27 for 27 bp = 0,27%).
 * @returns {string} The formatted percentage string.
 */
export function formatBasisPointsToPercentage(
  value: number | null | undefined,
  digits = 2,
  removeUnit?: boolean,
): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return formatPercentage(value / 100, digits, removeUnit);
}

/**
 * Formats a number into basis points format.
 * @param {number} value - The number to format (e.g. 27 for 27 bp).
 * @param {number} digits - Number of decimal places to display (default 0).
 * @param {boolean} removeUnit - Whether to remove the 'bp' unit suffix.
 * @returns {string} The formatted basis points string.
 */
export function formatBasisPoints(
  value: number | null | undefined,
  digits = 0,
  removeUnit = false,
): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  const formatted = new Intl.NumberFormat('da-DK', {
    maximumFractionDigits: digits,
  }).format(value);
  return removeUnit ? formatted : `${formatted} bp`;
}
/**
 * Formats a numeric value as a currency string.
 *
 * @param {number|undefined} value - The numeric value to format. Optional and defaults to undefined.
 * @param {'short' | 'long' | undefined} compactDisplay - Whether to format 'short' or 'long'. Optional and defaults to 'short'.
 * @returns {string|null} The compact formatted string.
 */
export function formatCompact(
  value?: number,
  compactDisplay?: 'short' | 'long' | undefined,
): string {
  if (!value || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('da-DK', {
    notation: 'compact',
    compactDisplay: compactDisplay ?? 'short',
  }).format(value);
}

/**
 * Formats the record size value from bytes to a human-readable format.
 * @param {number} recordSize - The size of the record in bytes.
 * @returns {string} The formatted record size with the appropriate unit.
 */
export function formatFileSize(recordSize?: number | null | undefined): string {
  const sizeUnits: string[] = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  if (
    recordSize === undefined ||
    recordSize === null ||
    Number.isNaN(recordSize)
  )
    return '—';
  let size: number = recordSize;
  let unitIndex: number = 0;

  while (size >= 1024 && unitIndex < sizeUnits.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  const formattedSize: string = `${size.toFixed(2)} ${sizeUnits[unitIndex]}`;
  return formattedSize;
}

/**
 * Checks if the target amount is within the variance range of the reference amount.
 *
 * @param {Object} params - The parameters object.
 * @param {number | null | undefined} params.targetAmount - The target amount to be checked.
 * @param {number | null | undefined} params.referenceAmount - The reference amount to compare against.
 * @param {number} params.variancePercentage - The percentage of variance allowed.
 *
 * @returns {boolean} - Returns true if the target amount is within the variance range of the reference amount, false otherwise.
 *
 * @throws {TypeError} - If any of the parameters are missing or not of the correct type.
 */
export function isAmountWithinVarianceRange({
  targetAmount,
  referenceAmount,
  variancePercentage,
}: {
  targetAmount: number | null | undefined;
  referenceAmount: number | null | undefined;
  variancePercentage: number | null | undefined;
}): boolean {
  if (!variancePercentage) return targetAmount === referenceAmount;
  if (
    targetAmount === undefined ||
    targetAmount === null ||
    referenceAmount === undefined ||
    referenceAmount === null ||
    Number.isNaN(targetAmount) ||
    Number.isNaN(referenceAmount) ||
    Number.isNaN(variancePercentage)
  ) {
    return false;
  }

  const variance = (Math.abs(referenceAmount) / 100) * variancePercentage;
  return Boolean(
    targetAmount < referenceAmount + variance &&
      targetAmount > referenceAmount - variance,
  );
}

/**
 * Formats a numeric value as an abbreviated value string as required for the Progress Bars in Engagement Overview.
 *
 * @param {number} value - The numeric value to format as currency. E.g.: 16000000 => 16 mio. / 1500 => 1,5 t
 * @param {FormatCurrencyOptions|undefined} options - Optional options object.
 * @returns {string} The formatted currency string.
 */
export function formatLargeNumber(number): string {
  if (number >= 1000) {
    const formatter = new Intl.NumberFormat('da', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumSignificantDigits: 3,
    });
    return formatter.format(number);
  } else {
    return number.toLocaleString('da-DK');
  }
}

/**
 * Formats an 8-digit Danish phone number with a space after 4 digits.
 *
 * @param {string | number | null | undefined} phoneNumber - The 8-digit phone number to format.
 * @returns {string} The formatted phone number string (e.g., "3311 1511") or "—" for invalid input.
 *
 * @example
 * // Returns "3311 1511"
 * formatPhoneNumber("33111511");
 *
 * @example
 * // Returns "3311 1511"
 * formatPhoneNumber(33111511);
 *
 * @example
 * // Returns "—"
 * formatPhoneNumber(null);
 */
export function formatPhoneNumber(
  phoneNumber: string | number | null | undefined,
): string {
  if (phoneNumber === undefined || phoneNumber === null || phoneNumber === '') {
    return '—';
  }

  // Convert to string and remove all non-digit characters
  const digitsOnly = String(phoneNumber).replace(/\D/g, '');

  // Must be exactly 8 digits
  if (digitsOnly.length !== 8) return '—';

  // Format: Insert space after first 4 digits
  return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4)}`;
}

/**
 * Calculates the sum of decimal values in an array, handling potential null or undefined values.
 *
 * @param {number[]} values - An array of numeric values to be summed.
 * @returns {number} The total sum of the values, converted to a standard number.
 *
 * @example
 * // Returns 10
 * sumDecimalValues([3, 4, 3]);
 *
 * @example
 * // Returns 7 (null and undefined values are treated as 0)
 * sumDecimalValues([3, null, 4, undefined]);
 */
export function sumDecimalValues(
  values: (number | null | undefined)[],
): number {
  return values
    .reduce((total, value) => total.plus(value || 0), new Decimal(0))
    .toNumber();
}
